import {
  MRT_ColumnFilterFnsState,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MaterialReactTable,
} from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";

import { useSysAccountsQuery } from "@/api/hooks/sys-account-hooks";
import { ACC_TP } from "@/components/config-modals/router-edit-modal/constants";
import { assembleKeyFilters, assembleSort } from "@/lib/cfg-utils";
import { COMMON_REFETCH_INTERVAL } from "@/lib/constants";
import { useOrderedColumns } from "@/lib/util-hooks";
import ServiceAccountsTopToolbar from "@/module/accounts-module/components/service-accounts-top-toolbar";
import { useAccountsTable } from "@/module/accounts-module/hooks/accounts-table-hook";
import { useServiceAccountsTableColumns } from "@/module/accounts-module/service-accounts-table/service-accounts-table-columns";
import { KeyFilter, KeySort } from "@/shared/api";

const requiredFilters: KeyFilter[] = [
  { key: "data.account_type", mode: "STRICT_IN", value: [ACC_TP.GRABBER, ACC_TP.SCANNER, ACC_TP.MANAGER] as unknown as object },
];

/*
const requiredFilters: KeyFilter[] = [
  { key: "data.account_type", mode: "NOT_EQUALS", value: [ACC_TP.REGULAR, ACC_TP.UNREGISTERED] as unknown as object },
];
*/

const ServiceAccountsTable = ({
  roomName,
  isOpen = true,
  setTotalAccountsNumber,
}: {
  roomName: string;
  isOpen?: boolean;
  setTotalAccountsNumber: (numberAccounts: number) => void;
}) => {
  const accountsColumns = useServiceAccountsTableColumns({ room: roomName });
  const columns = useOrderedColumns(accountsColumns);

  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "created_at", desc: true }]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [columnFilterFns, setColumnFilterFns] = useState<MRT_ColumnFilterFnsState>(
    Object.fromEntries(columns.map(({ id }) => [id, "contains"]))
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  const filters = useMemo<Set<KeyFilter>>(
    () => assembleKeyFilters(columnFilters, columnFilterFns, requiredFilters),
    [columnFilters, columnFilterFns]
  );

  const sort = useMemo<KeySort[]>(() => assembleSort(sorting), [sorting]);

  const {
    data: sysAccountsResponse,
    isError: isSysAccountsError,
    isLoading: isSysAccountsLoading,
    isRefetching: isSysAccountsRefetching,
    refetch: refetchSysAccounts,
  } = useSysAccountsQuery(
    {
      domain: roomName,
      filters: filters,
      pagination,
      sort: sort,
    },
    {
      enabled: isOpen,
      refetchInterval: COMMON_REFETCH_INTERVAL,
    }
  );

  useEffect(() => {
    !!sysAccountsResponse?.data.totalElements && setTotalAccountsNumber(sysAccountsResponse?.data.totalElements);
  }, [sysAccountsResponse?.data.totalElements]);

  const table = useAccountsTable({
    name: "serviceAccounts",
    columns: columns,
    data: sysAccountsResponse?.data.content ?? [],
    isDataLoading: isSysAccountsLoading,
    isDataRefetching: isSysAccountsRefetching,
    isDataLoadingError: isSysAccountsError,
    loadingErrorMessage: sysAccountsResponse?.statusText,
    totalElements: sysAccountsResponse?.data.totalElements,
    totalPages: sysAccountsResponse?.data.totalPages,
    pagination: pagination,
    setPagination: setPagination,
    columnFilters: columnFilters,
    setColumnFilters: setColumnFilters,
    columnFilterFns: columnFilterFns,
    setColumnFilterFns: setColumnFilterFns,
    sorting: sorting,
    setSorting: setSorting,
    renderToolbarCustomActions: ({ table }) => (
      <ServiceAccountsTopToolbar
        table={table}
        roomName={roomName}
        filters={filters}
        refetchAccounts={() => refetchSysAccounts()}
        totalElementsCount={sysAccountsResponse?.data.totalElements ?? 0}
      />
    ),
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [table, columnFilters, sorting, pagination]);

  return <MaterialReactTable table={table} />;
};

export default ServiceAccountsTable;
